import { atom } from "jotai";
import { atomWithImmer } from "jotai-immer";

import type {
  DataTableColumnFilter,
  DataTableFilterOption,
} from "@frontend/components/organisms/DataTable";
import { ApiError } from "@frontend/lib/http";
import { loadReferences } from "@frontend/lib/references";
import type { Reference } from "@frontend/lib/references";

import { projectApi } from "../services";
import type { ProjectListQuery } from "../services";
import type {
  ProjectListColumn,
  ProjectListDirection,
  ProjectListFilter,
  ProjectPage,
  ProjectRow,
  ProjectType,
} from "../types";

export type ProjectListFilterOptions = {
  serviceGroups: DataTableFilterOption[];
  states: DataTableFilterOption[];
  statuses: DataTableFilterOption[];
};

export type ProjectListState = {
  rows: ProjectRow[];
  total: number;
  page: number;
  perPage: number;
  search: string;
  type: ProjectType | null;
  filter: ProjectListFilter;
  sort: ProjectListColumn | null;
  direction: ProjectListDirection;
  criteria: DataTableColumnFilter[];
  filterOptions: ProjectListFilterOptions;
  loading: boolean;
  error: string | null;
};

export const initialProjectListState: ProjectListState = {
  rows: [],
  total: 0,
  page: 1,
  perPage: 25,
  search: "",
  type: null,
  filter: "active",
  sort: null,
  direction: "asc",
  criteria: [],
  filterOptions: { serviceGroups: [], statuses: [], states: [] },
  loading: true,
  error: null,
};

export const projectListAtom = atomWithImmer<ProjectListState>(
  initialProjectListState,
);

const PAGE_CACHE_LIMIT = 3;

type ProjectListSession = {
  draw: number;
  cache: Map<string, ProjectPage>;
  inFlight: Map<string, Promise<ProjectPage>>;
};

const projectListSessionAtom = atom<ProjectListSession>(() => ({
  draw: 0,
  cache: new Map(),
  inFlight: new Map(),
}));

type ProjectListRequest = Required<ProjectListQuery>;

function toRequest(state: ProjectListState): ProjectListRequest {
  const { search, type, filter, page, perPage, sort, direction, criteria } =
    state;

  return {
    search,
    type,
    filter,
    page,
    perPage,
    sort,
    direction,
    criteria,
    closureWeeksYear: null,
    responsableId: null,
  };
}

function fetchPage(
  session: ProjectListSession,
  query: ProjectListRequest,
): Promise<ProjectPage> {
  const key = JSON.stringify(query);
  const pending = session.inFlight.get(key);

  if (pending) {
    return pending;
  }

  const promise = projectApi
    .listProjects(query)
    .then((answer) => {
      remember(session, key, answer);

      return answer;
    })
    .finally(() => session.inFlight.delete(key));

  session.inFlight.set(key, promise);

  return promise;
}

function remember(
  session: ProjectListSession,
  key: string,
  answer: ProjectPage,
): void {
  session.cache.delete(key);
  session.cache.set(key, answer);

  while (session.cache.size > PAGE_CACHE_LIMIT) {
    const oldest = session.cache.keys().next().value;

    if (undefined === oldest) {
      break;
    }

    session.cache.delete(oldest);
  }
}

function recall(
  session: ProjectListSession,
  key: string,
): ProjectPage | undefined {
  const answer = session.cache.get(key);

  if (answer) {
    // Read again means kept longer.
    session.cache.delete(key);
    session.cache.set(key, answer);
  }

  return answer;
}

export const loadProjectListAtom = atom(
  null,
  async (get, set): Promise<void> => {
    const session = get(projectListSessionAtom);
    const query = toRequest(get(projectListAtom));
    const key = JSON.stringify(query);
    const draw = (session.draw += 1);

    const apply = (answer: ProjectPage) => {
      if (draw !== session.draw) {
        return;
      }

      set(projectListAtom, (draft) => {
        draft.rows = answer.items;
        draft.total = answer.total;
        draft.page = answer.page;
        draft.perPage = answer.perPage;
        draft.loading = false;
        draft.error = null;
      });
    };

    const cached = recall(session, key);

    if (cached) {
      apply(cached);

      return;
    }

    set(projectListAtom, (draft) => {
      draft.loading = true;
      draft.error = null;
    });

    try {
      apply(await fetchPage(session, query));
    } catch (error: unknown) {
      if (draw !== session.draw) {
        return;
      }

      set(projectListAtom, (draft) => {
        draft.loading = false;
        draft.error = message(error);
      });
    }
  },
);

export const reloadProjectListAtom = atom(
  null,
  async (get, set): Promise<void> => {
    get(projectListSessionAtom).cache.clear();

    await set(loadProjectListAtom);
  },
);

export const prefetchProjectListAtom = atom(
  null,
  (
    get,
    _set,
    query: Partial<Pick<ProjectListState, "page" | "sort" | "direction">>,
  ): void => {
    const state = get(projectListAtom);
    const session = get(projectListSessionAtom);
    const request = { ...toRequest(state), ...query };
    const pageCount = Math.max(1, Math.ceil(state.total / state.perPage));

    if (request.page < 1 || request.page > pageCount) {
      return;
    }

    if (session.cache.has(JSON.stringify(request))) {
      return;
    }

    fetchPage(session, request).catch(() => {
      // The click that may follow asks again, and reports on its own.
    });
  },
);

export const setProjectListQueryAtom = atom(
  null,
  async (
    _get,
    set,
    query: Partial<
      Pick<
        ProjectListState,
        | "search"
        | "type"
        | "filter"
        | "page"
        | "perPage"
        | "sort"
        | "direction"
        | "criteria"
      >
    >,
  ): Promise<void> => {
    set(projectListAtom, (draft) => {
      Object.assign(draft, query);

      if (undefined === query.page) {
        draft.page = 1;
      }
    });

    await set(loadProjectListAtom);
  },
);

export const loadProjectListOptionsAtom = atom(
  null,
  async (_get, set): Promise<void> => {
    try {
      const [serviceGroups, statuses, states] = await Promise.all([
        loadReferences("serviceGroups"),
        loadReferences("projectStatuses"),

        projectApi.listInterventionStates(),
      ]);

      set(projectListAtom, (draft) => {
        draft.filterOptions = {
          serviceGroups: toOptions(serviceGroups),
          statuses: toOptions(statuses),
          states: states.map(({ state, label }) => ({
            value: state,
            label,
          })),
        };
      });
    } catch {
      // The select filters stay empty, and the list itself still reads.
    }
  },
);

export const archiveProjectAtom = atom(
  null,
  async (_get, set, projectId: number): Promise<void> => {
    try {
      await projectApi.archiveProject(projectId);
    } catch (error: unknown) {
      set(projectListAtom, (draft) => {
        draft.error = message(error);
      });

      return;
    }

    await set(reloadProjectListAtom);
  },
);

export const deleteProjectAtom = atom(
  null,
  async (_get, set, projectId: number): Promise<void> => {
    try {
      await projectApi.deleteProject(projectId);
    } catch (error: unknown) {
      set(projectListAtom, (draft) => {
        draft.error = message(error);
      });

      return;
    }

    await set(reloadProjectListAtom);
  },
);

function toOptions(references: Reference[]): DataTableFilterOption[] {
  return references.map(({ id, label }) => ({ value: String(id), label }));
}

function message(error: unknown): string {
  if (error instanceof ApiError && 0 < error.violations.length) {
    return error.violations.map((violation) => violation.message).join(" ");
  }

  return error instanceof Error ? error.message : String(error);
}
